import { FormGroup } from '@angular/forms';
import {
  FormRule,
  FormRuleEffect,
  FormRuleFactory,
  RuleAction,
} from 'src/app/shared/forms';
import { SubmissionConstants } from '../../models/constants/submissions-constants';

export class CustomerInfoRules {
  constructor(
    private ruleFact: FormRuleFactory,
    private submissionsConst: SubmissionConstants
  ) {}

  getCustomRules(subValues: FormGroup): FormRule[] {
    const status = subValues.get('status')?.value;
    return [
      ...this.getCustomerNameRules(subValues, status),
      ...this.getStreetAddressRules(subValues, status),
      ...this.getCityRules(subValues, status),
      ...this.getStateRules(subValues, status),
      ...this.getZipCodeRules(subValues, status),
    ];
  }

  private getCustomerNameRules(
    subValues: FormGroup,
    status: string
  ): FormRule[] {
    const applyToCustomerName = (action: RuleAction, value?: string) =>
      this.createBaseEffect(action, 'ownerFullName', value);
    const trimmedValue = (subValues.get('ownerFullName')?.value ?? '').trim();
    if (status === this.submissionsConst.statusReadyToSubmit || status === this.submissionsConst.statusSubmittedManually) {
      return [
        this.ruleFact.createRule(`${trimmedValue.length === 0}`, [
          applyToCustomerName(RuleAction.MAKE_INVALID),
        ]),
      ];
    } else {
      return [
        this.ruleFact.createRule(`${true}`, [
          applyToCustomerName(RuleAction.CLEAR_ERROR_MESSAGE),
        ]),
      ];
    }
  }

  private getStreetAddressRules(
    subValues: FormGroup,
    status: string
  ): FormRule[] {
    const applyToStreetAddress = (action: RuleAction, value?: string) =>
      this.createBaseEffect(action, 'homeStreet', value);

    const trimmedValue = (subValues.get('homeStreet')?.value ?? '').trim();

    if (status === this.submissionsConst.statusReadyToSubmit || status === this.submissionsConst.statusSubmittedManually) {
      return [
        this.ruleFact.createRule(`${trimmedValue.toLowerCase() === 'tbd'}`, [
          applyToStreetAddress(RuleAction.MAKE_INVALID),
          applyToStreetAddress(
            RuleAction.SET_ERROR_MESSAGE,
            this.submissionsConst.streetAddressWarning
          ),
        ]),
        this.ruleFact.createRule(`${!trimmedValue.length}`, [
          applyToStreetAddress(RuleAction.MAKE_INVALID),
        ]),
      ];
    } else {
      return [
        this.ruleFact.createRule(`${true}`, [
          applyToStreetAddress(RuleAction.CLEAR_ERROR_MESSAGE),
        ]),
      ];
    }
  }

  private getCityRules(subValues: FormGroup, status: string): FormRule[] {
    const applyToCity = (action: RuleAction, value?: string) =>
      this.createBaseEffect(action, 'homeCity', value);
    const trimmedValue = (subValues.get('homeCity')?.value ?? '').trim();

    if (status === this.submissionsConst.statusReadyToSubmit || status === this.submissionsConst.statusSubmittedManually) {
      return [
        this.ruleFact.createRule(`${trimmedValue.length === 0}`, [
          applyToCity(RuleAction.MAKE_INVALID),
        ]),
      ];
    } else {
      return [
        this.ruleFact.createRule(`${true}`, [
          applyToCity(RuleAction.CLEAR_ERROR_MESSAGE),
        ]),
      ];
    }
  }

  private getStateRules(subValues: FormGroup, status: string): FormRule[] {
    const applyToState = (action: RuleAction, value?: string) =>
      this.createBaseEffect(action, 'homeState', value);
    const trimmedValue = (subValues.get('homeState')?.value ?? '').trim();

    if (status === this.submissionsConst.statusReadyToSubmit || status === this.submissionsConst.statusSubmittedManually) {
      return [
        this.ruleFact.createRule(`${trimmedValue.length === 0}`, [
          applyToState(RuleAction.MAKE_INVALID),
        ]),
      ];
    } else {
      return [
        this.ruleFact.createRule(`${true}`, [
          applyToState(RuleAction.CLEAR_ERROR_MESSAGE),
        ]),
      ];
    }
  }

  private getZipCodeRules(subValues: FormGroup, status: string): FormRule[] {
    const applyToZip = (action: RuleAction, value?: string) =>
      this.createBaseEffect(action, 'homePostalCode', value);
    const zipCode = subValues.get('homePostalCode')?.value;
    const isZipCodeValid = new RegExp(/^\d{5}(-\d{4})?$/).test(zipCode);

    if (status === this.submissionsConst.statusReadyToSubmit || status === this.submissionsConst.statusSubmittedManually) {
      return [
        this.ruleFact.createRule(`(${zipCode != null && !isZipCodeValid})`, [
          applyToZip(RuleAction.MAKE_INVALID),
          applyToZip(RuleAction.SET_ERROR_MESSAGE, this.submissionsConst.zipCodeError),
        ]),
      ];
    } else {
      return [
        this.ruleFact.createRule(`${true}`, [
          applyToZip(RuleAction.CLEAR_ERROR_MESSAGE),
        ]),
      ];
    }
  }

  private createBaseEffect(
    action: RuleAction,
    controlToApplyTo: string,
    value?: string
  ): FormRuleEffect {
    return {
      action: action,
      affectingType: 'control',
      applyTo: controlToApplyTo,
      value: value,
    };
  }
}
